import React, { useEffect } from 'react';
import { useResumeContext } from '../../context/ResumeContext';

const IntroductionModule = () => {
  const { markSectionViewed } = useResumeContext();
  
  // Mark this section as viewed when component mounts
  useEffect(() => {
    markSectionViewed('intro');
  }, [markSectionViewed]);

  return (
    <div className="space-y-6">
      <section className="card border-l-4 border-blue-500">
        <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
          📚 Welcome to My Instructional Journey
        </h2>
        <p className="mb-4">
          This interactive resume is designed as a learning experience that demonstrates both my 
          professional qualifications and my approach to instructional design. As you explore this 
          CV, you'll encounter principles of adult learning, visual design, and interactive engagement.
        </p>
        
        <div className="bg-blue-50 p-4 rounded-lg mb-4">
          <h3 className="font-bold text-blue-800 mb-2">After reviewing this resume, you will be able to:</h3>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Identify my key qualifications and experience in technical documentation and instructional content</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Recognize how my skills transfer effectively to instructional design roles</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Understand my approach to transforming complex information into engaging learning experiences</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Evaluate my potential fit with your instructional design needs</span>
            </li>
          </ul>
        </div>
        
        <div className="flex justify-between items-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <div className="flex items-center gap-2 text-yellow-800">
            <span>ℹ️</span>
            <span className="font-medium">Navigation Tip</span>
          </div>
          <p className="text-yellow-700">
            Use the tabs above to explore different sections, and click on expandable elements to reveal more information.
          </p>
        </div>
      </section>
      
      <section className="card">
        <h2 className="text-xl font-bold mb-4">Professional Summary</h2>
        <p className="mb-4">
          Experienced learning content developer with 7+ years creating comprehensive technical documentation, 
          educational materials, and training programs. Skilled in transforming complex technical concepts 
          into accessible, engaging content for diverse audiences.
        </p>
        
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
              ⭐ Core Strengths
            </h3>
            <ul className="space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-blue-500">→</span>
                <span>Technical content simplification</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500">→</span>
                <span>Visual learning design</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500">→</span>
                <span>Process documentation</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500">→</span>
                <span>Training development</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
              ⏱️ Career Highlights
            </h3>
            <ul className="space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-blue-500">→</span>
                <span>Author of comprehensive technical manuals</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500">→</span>
                <span>Creator of quarterly technical webinar series</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500">→</span>
                <span>Featured speaker at industry events</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500">→</span>
                <span>Developer of interactive learning tools</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IntroductionModule;
